<template>
  <div class="chart-card">
    <div class="chart-header">
      <div class="chart-title-block">
        <span class="chart-kicker">COMPETITIVIDAD</span>
        <h3 class="chart-title">Evolución últimos {{ dias }} días</h3>
      </div>
      <div class="range-selector">
        <button v-for="d in [7, 30, 90]" :key="d" :class="{ active: dias === d }" @click="dias = d">
          {{ d }}D
        </button>
      </div>
    </div>

    <!-- IMPORTANTE: el wrapper SIEMPRE existe (no v-if) para que Chart.js mida bien -->
    <div class="chart-canvas-wrapper">
      <div v-if="loading" class="chart-state">Cargando…</div>
      <div v-else-if="serie.length === 0" class="chart-state">Sin datos en el rango seleccionado</div>
      <canvas v-show="!loading && serie.length > 0" ref="canvasRef"></canvas>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick, defineProps } from 'vue';
import axios from 'axios';
import * as Chart from 'chart.js';

Chart.Chart.register(...Chart.registerables);

const props = defineProps({
  vista: { type: String, default: 'web' }
});

const API = 'https://dash-visaovip.kompara.tech/api';
const getH = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token_precios') } });

const dias = ref(30);
const serie = ref([]);
const loading = ref(true);
const canvasRef = ref(null);
let chartInstance = null;

const load = async () => {
  loading.value = true;
  try {
    const res = await axios.get(`${API}/stats/competitividad-historica`, {
      ...getH(),
      params: { dias: dias.value, vista: props.vista }
    });
    serie.value = res.data.serie || [];
  } catch (e) {
    console.error('Error cargando competitividad:', e);
    serie.value = [];
  } finally {
    loading.value = false;
    // Esperar que el v-show actualice el DOM y el wrapper tenga tamaño
    await nextTick();
    // Doble setTimeout para asegurar que CSS layout terminó
    setTimeout(() => {
      renderChart();
    }, 50);
  }
};

const renderChart = () => {
  if (!canvasRef.value || serie.value.length === 0) return;
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }

  const labels = serie.value.map(s => s.fecha.slice(5));  // MM-DD
  const data = serie.value.map(s => s.competitividad_pct);

  // Detectar tema actual
  const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
  const textColor = isDark ? '#a1a1aa' : '#52525b';
  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const accentColor = isDark ? '#fbbf24' : '#d97706';

  chartInstance = new Chart.Chart(canvasRef.value, {
    type: 'line',
    data: {
      labels,
      datasets: [{
        label: '% Competitividad',
        data,
        borderColor: accentColor,
        backgroundColor: accentColor + '22',
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: accentColor,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(13, 16, 22, 0.95)',
          titleColor: accentColor,
          bodyColor: '#fafafa',
          titleFont: { family: 'JetBrains Mono', size: 11, weight: '700' },
          bodyFont: { family: 'JetBrains Mono', size: 11 },
          padding: 10,
          cornerRadius: 2,
          callbacks: {
            label: (ctx) => `${ctx.parsed.y}%`
          }
        }
      },
      scales: {
        x: {
          ticks: { color: textColor, font: { family: 'JetBrains Mono', size: 9 } },
          grid: { color: gridColor }
        },
        y: {
          beginAtZero: false,
          ticks: {
            color: textColor,
            font: { family: 'JetBrains Mono', size: 9 },
            callback: (v) => v + '%'
          },
          grid: { color: gridColor }
        }
      }
    }
  });

  // Forzar resize por si el wrapper cambió de tamaño
  setTimeout(() => {
    if (chartInstance) chartInstance.resize();
  }, 100);
};

watch(() => props.vista, load);
watch(dias, load);

onMounted(load);
</script>

<style scoped>
.chart-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 3px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.chart-header {
  padding: 10px 14px;
  background: var(--color-surface-3);
  border-bottom: 1px solid var(--color-accent-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.chart-title-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.chart-kicker {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: var(--color-accent);
  text-transform: uppercase;
}
.chart-title {
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text);
}
.range-selector {
  display: flex;
  gap: 2px;
  background: var(--color-background);
  border: 1px solid var(--color-border-strong);
  padding: 2px;
  border-radius: 2px;
}
.range-selector button {
  background: transparent;
  border: none;
  padding: 3px 9px;
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--color-text-muted);
}
.range-selector button.active {
  background: var(--color-accent-bg);
  color: var(--color-accent);
  border: 1px solid var(--color-accent-border);
}

.chart-canvas-wrapper {
  position: relative;
  height: 240px;
  padding: 12px 14px;
  /* Importante: el wrapper define el tamaño que Chart.js debe usar */
  box-sizing: border-box;
}
.chart-canvas-wrapper canvas {
  /* Forzar al canvas a llenar el wrapper */
  max-width: 100%;
  max-height: 100%;
}
.chart-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  font-size: 11px;
  font-style: italic;
}
</style>